/**
 * Servicio de sesiones.
 *
 * Es la capa que puede ver a la vez el curriculum, el dominio y la
 * persistencia. `domain/` no puede importar `curriculum/` y `persistencia/` no
 * conoce el contenido pedagógico, así que la composición vive aquí.
 *
 * REGLA DURA: nunca se entra al modo foco con una sesión que solo existe en
 * memoria. `iniciarSesion` no devuelve hasta que la escritura local terminó.
 */

import { CURRICULUM_ACTUAL, VERSION_CURRICULUM_ACTUAL } from '../curriculum';
import type { ClaveSesion, Duracion, Plan } from '../curriculum/tipos';
import type { DuracionSesion } from '../domain/modelo/ajustes';
import type { EtiquetaGrabacion } from '../domain/modelo/grabacion';
import type {
  PasoRegistrado,
  SemanaCheckpoint,
  SesionRegistrada,
  TipoSesion,
} from '../domain/modelo/sesion';
import { FUENTES_LISTENING } from '../curriculum/v1/fuentesListening';
import { elegirFuenteListening } from '../domain/reglas/checkpoints';
import { cerrarSesion } from '../domain/reglas/sesionActiva';
import {
  semanaActual,
  siguienteRecomendada,
  type Recomendacion,
} from '../domain/reglas/progresion';
import { sesionesRepo } from '../persistencia/sesionesRepo';

/**
 * Instantánea de los pasos del plan, tal como estaban al empezar.
 *
 * El primer paso queda marcado como entrado: empezar la sesión ES entrar en él.
 * Registrarlo aquí evita un efecto que escriba al montar el modo foco.
 */
export function instantaneaDePasos(plan: Plan, ahora: Date = new Date()): PasoRegistrado[] {
  return plan.pasos.map((paso, indice) => ({
    stepKey: paso.clave,
    completado: false,
    omitido: false,
    segundosAcumulados: 0,
    corriendoDesde: null,
    ...(indice === 0 ? { iniciadoEn: ahora.toISOString() } : {}),
  }));
}

/** Traduce la recomendación a la clave de sesión del curriculum. */
export function claveDeRecomendacion(recomendacion: Recomendacion): ClaveSesion {
  switch (recomendacion.tipo) {
    case 'baseline':
      return 'baseline';
    case 'checkpoint':
      return 'checkpoint';
    case 'dia':
      return recomendacion.sessionKey as ClaveSesion;
    case 'reanudar':
      return recomendacion.sesion.sessionKey as ClaveSesion;
  }
}

const TIPO_POR_CLAVE: Record<string, TipoSesion> = {
  baseline: 'baseline',
  checkpoint: 'checkpoint',
};

function semanaDeCheckpoint(
  recomendacion: Recomendacion,
): SemanaCheckpoint | undefined {
  if (recomendacion.tipo === 'baseline') return 0;
  if (recomendacion.tipo === 'checkpoint') {
    return recomendacion.semana as SemanaCheckpoint;
  }
  return undefined;
}

export interface ResultadoInicio {
  sesion: SesionRegistrada;
  /** `true` si ya existía una sesión abierta y se devuelve esa en su lugar. */
  reanudada: boolean;
}

/**
 * Empieza una sesión y la deja PERSISTIDA antes de devolver.
 *
 * Si ya hay una sesión en progreso, NO crea otra: devuelve la existente. Crear
 * una segunda dejaría dos sesiones abiertas compitiendo por ser «la activa».
 */
export async function iniciarSesion(
  duracion: DuracionSesion,
): Promise<ResultadoInicio> {
  const sesiones = await sesionesRepo.listar();

  const recomendacion = siguienteRecomendada(sesiones);
  if (recomendacion.tipo === 'reanudar') {
    return { sesion: recomendacion.sesion, reanudada: true };
  }

  const clave = claveDeRecomendacion(recomendacion);
  const sesionCurriculum = CURRICULUM_ACTUAL.sesiones[clave];
  const plan = sesionCurriculum.planes[duracion as Duracion];

  // La fuente de listening se fija AL CREAR la medición, no al llegar al
  // paso: así queda registrada aunque la sesión se abandone, y la siguiente
  // medición no puede volver a caer en la misma. Solo baseline y checkpoints
  // consumen muestras auténticas.
  // Y solo si ESTE plan tiene de verdad un paso de muestra auténtica: los
  // planes de 20 minutos no lo tienen, y asignarles una fuente la gastaría
  // sin usarla, dejando menos material desconocido para las que sí miden.
  const usaMuestraAutentica = plan.pasos.some(
    (paso) => paso.herramienta === 'muestraListening',
  );
  const esMedicion =
    (clave === 'baseline' || clave === 'checkpoint') && usaMuestraAutentica;
  const fuente = esMedicion
    ? elegirFuenteListening(
        sesiones,
        FUENTES_LISTENING.map((f) => f.clave),
      )
    : null;

  const sesion = await sesionesRepo.crear({
    tipo: TIPO_POR_CLAVE[clave] ?? 'dia',
    sessionKey: clave,
    semana: semanaActual(sesiones),
    checkpointSemana: semanaDeCheckpoint(recomendacion),
    duracionPlanificada: duracion,
    curriculumVersion: VERSION_CURRICULUM_ACTUAL,
    // Agotadas todas, se deja sin fuente y la interfaz lo explica: es un
    // estado legítimo, no un error que deba impedir medir.
    ...(fuente?.clave ? { fuenteListeningClave: fuente.clave } : {}),
    pasos: instantaneaDePasos(plan),
  });

  return { sesion, reanudada: false };
}

/** Guarda la sesión. Se llama en CADA cambio, no al final. */
export async function guardarSesion(sesion: SesionRegistrada): Promise<SesionRegistrada> {
  return sesionesRepo.guardar(sesion);
}

/**
 * Cierra la sesión con el estado que le corresponde según lo que se hizo.
 *
 * Nunca marca como completado lo que se omitió. Una sola escritura atómica.
 */
export async function terminarSesion(
  sesion: SesionRegistrada,
  ahora: Date = new Date(),
): Promise<SesionRegistrada> {
  return sesionesRepo.guardar(cerrarSesion(sesion, ahora));
}

/** El plan del curriculum que corresponde a una sesión registrada. */
export function planDeSesion(sesion: SesionRegistrada): Plan {
  const delCurriculum = CURRICULUM_ACTUAL.sesiones[sesion.sessionKey as ClaveSesion];
  return delCurriculum.planes[sesion.duracionPlanificada as Duracion];
}

/**
 * Etiqueta que corresponde a la muestra de una sesión.
 *
 * Sale del TIPO de sesión, no del baseline en particular: el mismo motor de
 * grabación sirve para W0, W4, W8 y W12 sin tocar nada.
 */
export function etiquetaDeMuestra(sesion: SesionRegistrada): EtiquetaGrabacion {
  if (sesion.tipo === 'baseline') return 'baseline_w0';
  if (sesion.tipo === 'checkpoint') {
    if (sesion.checkpointSemana === 4) return 'checkpoint_w4';
    if (sesion.checkpointSemana === 8) return 'checkpoint_w8';
    if (sesion.checkpointSemana === 12) return 'checkpoint_w12';
  }
  if (sesion.sessionKey === 'd2') return 'practica_pronunciacion';
  return 'muestra_libre';
}

/** La sesión del curriculum que corresponde a una sesión registrada. */
export function definicionDeSesion(sesion: SesionRegistrada) {
  return CURRICULUM_ACTUAL.sesiones[sesion.sessionKey as ClaveSesion];
}
